import { useCallback, useState } from 'react';

const ACTION_TYPES = {
  SELECTION_CHANGE: 'SELECTION_CHANGE',
  RESET_SELECTION: 'RESET_SELECTION',
  SET_SELECTION: 'SET_SELECTION',
};

const selectionChange = ({ entity, checked }) => ({
  type: ACTION_TYPES.SELECTION_CHANGE,
  payload: { entity, checked },
});

const resetSelection = () => ({
  type: ACTION_TYPES.RESET_SELECTION,
});

const updateSelection = ({ selectedNodes, keepExisting }) => ({
  type: ACTION_TYPES.SET_SELECTION,
  payload: { selectedNodes, keepExisting },
});

const entitySelectionReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SELECTION_CHANGE:
      return action.payload.checked
        ? [...state, action.payload.entity]
        : state.filter((e) => e.id !== action.payload.entity.id);
    case ACTION_TYPES.RESET_SELECTION:
      return [];
    case ACTION_TYPES.SET_SELECTION:
      return action.payload.keepExisting
        ? [...state, ...action.payload.selectedNodes]
        : action.payload.selectedNodes;
    default:
      throw new Error();
  }
};

/**
 * this hook manages state changes for entity selection
 * the state can be stored in the hook or somewhere else using setSelectedEntities
 * multiple: boolean - whether multiple entities can be selected
 * selectedEntities?: Array<Entity> - optional, makes the state controlled instead of storing it in the hook
 * setSelectedEntities?: (Array<Entity>) => void - optional, makes the state controlled instead of storing it in the hook
 */
export default ({
  multiple,
  selectedEntities: selectedEntitiesProp,
  setSelectedEntities: setSelectedEntitiesProp,
}) => {
  const isControlled = selectedEntitiesProp && setSelectedEntitiesProp;
  const [uncontrolledSelectedEntities, setUncontrolledSelectedEntities] =
    useState([]);
  const controlledDispatch = useCallback(
    (action) => {
      setSelectedEntitiesProp(
        entitySelectionReducer(selectedEntitiesProp, action),
      );
    },
    [selectedEntitiesProp, setSelectedEntitiesProp],
  );
  const uncontrolledDispatch = useCallback((action) => {
    setUncontrolledSelectedEntities((prevState) =>
      entitySelectionReducer(prevState, action),
    );
  }, []);
  const dispatch = isControlled ? controlledDispatch : uncontrolledDispatch;

  const onSelectionChange = useCallback(
    ({ entity, checked }) =>
      dispatch(
        multiple
          ? selectionChange({ entity, checked })
          : updateSelection({ selectedNodes: [entity] }),
      ),
    [dispatch, multiple],
  );
  const setSelection = useCallback(
    (selectedNodes, keepExisting) =>
      dispatch(updateSelection({ selectedNodes, keepExisting })),
    [dispatch],
  );
  const clearSelection = useCallback(
    () => dispatch(resetSelection()),
    [dispatch],
  );

  return {
    selectedEntities: isControlled
      ? selectedEntitiesProp
      : uncontrolledSelectedEntities,
    onSelectionChange,
    setSelection,
    clearSelection,
  };
};
